import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  CircleAlert,
  Download,
  FilePlus2,
  Loader2,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { AddFilesDialog } from "@/components/hbol/add-files-dialog";
import { FilesPanel } from "@/components/hbol/files-panel";
import { RowsTable } from "@/components/hbol/rows-table";
import { HbolStatusBadge } from "@/components/hbol/status-badge";
import { PdfViewer } from "@/components/pdf/pdf-viewer";
import { EditableCell } from "@/components/review/editable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api";
import {
  describePages,
  describeTotals,
  hbolApi,
  hbolJobQuery,
  moved,
  PAGE_KIND_LABEL,
  type HbolFile,
  type HbolJob,
  type HbolRow,
  type RowPatch,
} from "@/lib/hbol";

export const Route = createFileRoute("/_authenticated/hbol/$jobId")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(hbolJobQuery(params.jobId)),
  component: HbolJobPage,
});

interface Viewing {
  fileId: string;
  page: number;
}

function firstView(job: HbolJob): Viewing | null {
  const row = job.rows.find((r) => r.source_file);
  if (row?.source_file) return { fileId: row.source_file, page: row.source_page ?? 1 };
  return job.files[0] ? { fileId: job.files[0].id, page: 1 } : null;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function HbolJobPage() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: job } = useSuspenseQuery(hbolJobQuery(jobId));
  const [viewing, setViewing] = useState<Viewing | null>(() => firstView(job));
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [tab, setTab] = useState("document");
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<"read" | "delete" | null>(null);
  const [showDocument, setShowDocument] = useState(true);

  const reading = job.status === "reading";
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["hbol"] });
  const setJob = (next: HbolJob) => queryClient.setQueryData(hbolJobQuery(jobId).queryKey, next);

  const updateRow = useMutation({
    mutationFn: ({ row, patch }: { row: HbolRow; patch: RowPatch }) =>
      hbolApi.updateRow(row.id, patch),
    onSuccess: refresh,
    onError: (err) => {
      toast.error(errorMessage(err, "Could not save"));
      void refresh();
    },
  });
  const addRow = useMutation({
    mutationFn: () => hbolApi.addRow(jobId, { house_bl: "" }),
    onSuccess: async (row) => {
      await refresh();
      setSelectedRow(row.id);
    },
    onError: (err) => toast.error(errorMessage(err, "Could not add a row")),
  });
  const deleteRow = useMutation({
    mutationFn: (row: HbolRow) => hbolApi.deleteRow(row.id),
    onSuccess: refresh,
    onError: (err) => toast.error(errorMessage(err, "Could not delete the row")),
  });
  const order = useMutation({
    mutationFn: (ids: string[]) => hbolApi.orderRows(jobId, ids),
    onSuccess: setJob,
    onError: (err) => toast.error(errorMessage(err, "Could not move the row")),
  });
  const rename = useMutation({
    mutationFn: (title: string) => hbolApi.rename(jobId, title),
    onSuccess: async (next) => {
      setJob(next);
      await refresh();
    },
    onError: (err) => toast.error(errorMessage(err, "Could not rename")),
  });
  const read = useMutation({
    mutationFn: () => hbolApi.read(jobId),
    onSuccess: (next) => {
      setJob(next);
      toast.success("Reading the documents again");
    },
    onError: (err) => toast.error(errorMessage(err, "Could not start reading")),
  });
  const removeFile = useMutation({
    mutationFn: (file: HbolFile) => hbolApi.removeFile(file.id),
    onSuccess: async (_, file) => {
      if (viewing?.fileId === file.id) setViewing(null);
      await refresh();
      toast.success(`${file.filename} removed; read again to update the rows`);
    },
    onError: (err) => toast.error(errorMessage(err, "Could not remove the file")),
  });
  const remove = useMutation({
    mutationFn: () => hbolApi.remove(jobId),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: hbolJobQuery(jobId).queryKey });
      await queryClient.invalidateQueries({ queryKey: ["hbol", "jobs"] });
      toast.success("Import deleted");
      void navigate({ to: "/hbol" });
    },
    onError: (err) => toast.error(errorMessage(err, "Could not delete")),
  });

  const file = job.files.find((f) => f.id === viewing?.fileId) ?? null;
  const page = file?.pages.find((p) => p.page === viewing?.page);
  const extracted = job.extracted;
  const manifest = extracted.manifest_totals;
  const jobWarnings = extracted.warnings ?? [];
  const facts = [
    extracted.container && `Container ${extracted.container}`,
    extracted.master_bl && `Master ${extracted.master_bl}`,
    extracted.forwarder,
    extracted.vessel_voyage,
  ].filter(Boolean);

  const showRow = (row: HbolRow) => {
    setSelectedRow(row.id);
    if (row.source_file) {
      setViewing({ fileId: row.source_file, page: row.source_page ?? 1 });
      setTab("document");
      setShowDocument(true);
    }
  };

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col gap-3">
      {/* header */}
      <div className="flex flex-wrap items-start gap-3">
        <Button asChild size="icon" variant="ghost" className="mt-0.5 size-8">
          <Link to="/hbol" aria-label="Back to imports">
            <ChevronLeft className="size-4" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <EditableCell
              value={job.title}
              placeholder={job.display_title}
              aria-label="Name"
              className="h-8 max-w-sm text-base font-semibold"
              onCommit={(v) => rename.mutate(v.trim())}
            />
            {reading && <Loader2 className="text-muted-foreground size-4 animate-spin" />}
            <HbolStatusBadge status={job.status} />
          </div>
          <p className="text-muted-foreground px-1 text-xs">
            {facts.length ? facts.join(" · ") : reading ? "Reading the documents…" : "—"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DownloadButton job={job} kind="csv" />
          <DownloadButton job={job} kind="xlsx" />
          <Button size="sm" variant="outline" disabled={reading} onClick={() => setAdding(true)}>
            <FilePlus2 className="size-4" /> Add files
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={reading || read.isPending || job.files.length === 0}
            onClick={() => setConfirm("read")}
          >
            <RefreshCw className="size-4" /> Read again
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-8"
            aria-label="Delete import"
            onClick={() => setConfirm("delete")}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      {/* what the reader could not do, and what does not add up */}
      {job.error && !reading && (
        <div
          role="alert"
          className="border-destructive/40 text-destructive rounded-md border px-3 py-2 text-sm whitespace-pre-line"
        >
          {job.error}
        </div>
      )}
      {jobWarnings.length > 0 && !reading && (
        <ul className="space-y-0.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          {jobWarnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <CircleAlert className="mt-0.5 size-3.5 shrink-0" />
              {w.message}
            </li>
          ))}
        </ul>
      )}

      <div
        className={
          showDocument
            ? "grid min-h-0 flex-1 grid-cols-[minmax(0,2fr)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)] gap-3"
            : "grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)]"
        }
      >
        {/* rows */}
        <section className="bg-background flex min-h-0 flex-col rounded-md border">
          <header className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b px-3 py-2 text-xs">
            <h2 className="font-semibold tracking-wide uppercase">House bills</h2>
            <span>
              {job.totals.rows} rows · {job.totals.quantity} pieces ·{" "}
              {describeTotals(job.totals.weight)} · {describeTotals(job.totals.volume)}
            </span>
            {manifest && (
              <span className="text-muted-foreground">
                Manifest: {manifest.pieces ?? "—"} pieces ·{" "}
                {manifest.weight !== null ? `${manifest.weight} ${manifest.weight_unit}` : "—"} ·{" "}
                {manifest.volume !== null ? `${manifest.volume} ${manifest.volume_unit}` : "—"}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                disabled={reading || addRow.isPending}
                onClick={() => addRow.mutate()}
              >
                <Plus className="size-3.5" /> Add row
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                title={showDocument ? "Hide the document" : "Show the document"}
                aria-label={showDocument ? "Hide the document" : "Show the document"}
                onClick={() => setShowDocument((v) => !v)}
              >
                {showDocument ? (
                  <PanelRightClose className="size-4" />
                ) : (
                  <PanelRightOpen className="size-4" />
                )}
              </Button>
            </div>
          </header>
          <div className="min-h-0 flex-1 overflow-auto">
            {reading && job.rows.length === 0 ? (
              <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm">
                <Loader2 className="size-4 animate-spin" />
                Sorting pages and reading the manifest and house bills…
              </div>
            ) : (
              <RowsTable
                rows={job.rows}
                selectedId={selectedRow}
                disabled={reading}
                onSelect={showRow}
                onChange={(row, patch) => updateRow.mutate({ row, patch })}
                onMove={(row, step) =>
                  order.mutate(
                    moved(
                      job.rows.map((r) => r.id),
                      row.id,
                      step,
                    ),
                  )
                }
                onDelete={(row) => deleteRow.mutate(row)}
              />
            )}
          </div>
        </section>

        {/* documents */}
        {showDocument && (
          <Tabs
            value={tab}
            onValueChange={setTab}
            className="bg-background flex min-h-0 flex-col rounded-md border"
          >
            <div className="flex items-center gap-2 border-b px-2 py-1">
              <TabsList>
                <TabsTrigger value="document">Document</TabsTrigger>
                <TabsTrigger value="files">Files ({job.files.length})</TabsTrigger>
              </TabsList>
              {tab === "document" && file && (
                <span className="text-muted-foreground min-w-0 truncate text-xs">
                  {file.filename}
                  {page &&
                    ` · ${PAGE_KIND_LABEL[page.kind]}${page.house_bl ? ` ${page.house_bl}` : ""}`}
                </span>
              )}
            </div>
            <TabsContent value="document" className="mt-0 min-h-0 flex-1">
              {file && viewing ? (
                <PdfViewer
                  key={file.url}
                  url={file.url}
                  page={viewing.page}
                  rotate={page?.upright}
                  onPageChange={(p) => setViewing({ fileId: file.id, page: p })}
                />
              ) : (
                <p className="text-muted-foreground p-4 text-sm">
                  Pick a row or a file to see its page.
                </p>
              )}
            </TabsContent>
            <TabsContent value="files" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
              <p className="text-muted-foreground mb-2 text-xs">
                {describePages(extracted.pages) || "Pages are sorted when the documents are read."}
              </p>
              <FilesPanel
                job={job}
                activeFileId={viewing?.fileId ?? null}
                disabled={reading}
                onOpen={(f, p) => {
                  setViewing({ fileId: f.id, page: p });
                  setTab("document");
                }}
                onRemove={(f) => removeFile.mutate(f)}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AddFilesDialog jobId={jobId} open={adding} onOpenChange={setAdding} />
      <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "delete" ? "Delete this import?" : "Read the documents again?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "delete"
                ? "The uploaded files and all rows are removed. This cannot be undone."
                : "Rows that were not changed by hand are replaced by a fresh reading. Rows you changed stay as they are."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => (confirm === "delete" ? remove.mutate() : read.mutate())}
            >
              {confirm === "delete" ? "Delete" : "Read again"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** The template as a file; a plain link so the browser handles the download itself. */
function DownloadButton({ job, kind }: { job: HbolJob; kind: "csv" | "xlsx" }) {
  const label = kind.toUpperCase();
  const variant = kind === "csv" ? "default" : "outline";
  if (job.rows.length === 0 || job.status === "reading") {
    return (
      <Button size="sm" variant={variant} disabled>
        <Download className="size-4" /> {label}
      </Button>
    );
  }
  return (
    <Button asChild size="sm" variant={variant}>
      <a href={hbolApi.exportUrl(job.id, kind)}>
        <Download className="size-4" /> {label}
      </a>
    </Button>
  );
}
